/**
 * Per-list sort vocabulary and comparators for the Tasks list screens.
 *
 * Design: [[tasks-list-sorting]].
 *
 * Two invariants matter here and both exist to stop rows moving when they
 * shouldn't:
 *
 *   1. The comparison chain is TOTAL — primary key, then due date, then
 *      `id`. No two distinct rows ever compare equal, so re-sorting the
 *      same rows always yields the same sequence.
 *   2. MISSING values never follow the direction flip. Undated and
 *      unassigned rows belong at the bottom whether you sort up or down;
 *      negating a comparator would float them to the top under `desc`.
 */

import { useCallback, useRef, useState } from 'react';
import { taskPriorityRank, taskStatusRank } from '@k-os/core';
import type { TaskDto } from '../api/tasks';

export type TaskSortKey =
  | 'native'
  | 'priority'
  | 'due'
  | 'created'
  | 'updated'
  | 'person'
  | 'project'
  | 'area'
  | 'status';

export type SortDir = 'asc' | 'desc';

export interface TaskSort {
  key: TaskSortKey;
  dir: SortDir;
}

export interface SortKeyMeta {
  key: TaskSortKey;
  label: string;
  /** Direction adopted when this key is newly selected. */
  natural: SortDir;
}

/** Menu order. `native` first — it is the "leave it alone" option. */
export const SORT_KEYS: SortKeyMeta[] = [
  { key: 'native', label: 'Default', natural: 'asc' },
  { key: 'priority', label: 'Priority', natural: 'asc' },
  { key: 'due', label: 'Due date', natural: 'asc' },
  { key: 'created', label: 'Created', natural: 'desc' },
  { key: 'updated', label: 'Updated', natural: 'desc' },
  { key: 'person', label: 'Person', natural: 'asc' },
  { key: 'project', label: 'Project', natural: 'asc' },
  { key: 'area', label: 'Area', natural: 'asc' },
  { key: 'status', label: 'Status', natural: 'asc' },
];

const SORT_KEY_SET = new Set<string>(SORT_KEYS.map((k) => k.key));

export function isTaskSortKey(v: unknown): v is TaskSortKey {
  return typeof v === 'string' && SORT_KEY_SET.has(v);
}

export function sortKeyLabel(key: TaskSortKey): string {
  return SORT_KEYS.find((k) => k.key === key)?.label ?? 'Default';
}

/** The direction a key adopts when freshly picked from the menu. */
export function naturalDir(key: TaskSortKey): SortDir {
  return SORT_KEYS.find((k) => k.key === key)?.natural ?? 'asc';
}

/**
 * `GET /tasks?sort=` equivalent for a client key, or `undefined` when the
 * server can't express it. Only All tasks uses this — it's the one list that
 * can realistically hit the server's 500-row cap, so its fetched window has
 * to match what's being sorted.
 */
export function serverSortFor(key: TaskSortKey): 'due' | 'created' | 'updated' | 'priority' | undefined {
  switch (key) {
    case 'due':
    case 'created':
    case 'updated':
    case 'priority':
      return key;
    default:
      return undefined;
  }
}

// --- comparator internals -------------------------------------------------

function refName(t: TaskDto, key: 'person' | 'project' | 'area'): string | null {
  const name = key === 'person' ? t.person?.name : key === 'project' ? t.project?.name : t.area?.name;
  return name && name.length > 0 ? name : null;
}

/** True when this row has nothing to compare on for `key`, so it sinks. */
function isMissing(t: TaskDto, key: TaskSortKey): boolean {
  switch (key) {
    case 'due':
      return t.dueAt === null;
    case 'person':
    case 'project':
    case 'area':
      return refName(t, key) === null;
    default:
      // priority, status, created and updated are non-nullable.
      return false;
  }
}

function num(a: number, b: number): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function timestamp(iso: string | null): number {
  return iso === null ? 0 : Date.parse(iso);
}

function comparePrimary(a: TaskDto, b: TaskDto, key: TaskSortKey): number {
  switch (key) {
    case 'priority':
      return num(taskPriorityRank(a.priority), taskPriorityRank(b.priority));
    case 'status':
      return num(taskStatusRank(a.status), taskStatusRank(b.status));
    case 'due':
      return num(timestamp(a.dueAt), timestamp(b.dueAt));
    case 'created':
      return num(timestamp(a.createdAt), timestamp(b.createdAt));
    case 'updated':
      return num(timestamp(a.updatedAt), timestamp(b.updatedAt));
    case 'person':
    case 'project':
    case 'area': {
      const an = refName(a, key) ?? '';
      const bn = refName(b, key) ?? '';
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    }
    default:
      return 0;
  }
}

function compareDueAscNullsLast(a: TaskDto, b: TaskDto): number {
  const aMissing = a.dueAt === null;
  const bMissing = b.dueAt === null;
  if (aMissing !== bMissing) return aMissing ? 1 : -1;
  if (aMissing) return 0;
  return num(timestamp(a.dueAt), timestamp(b.dueAt));
}

function compareId(a: TaskDto, b: TaskDto): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function compareTasks(a: TaskDto, b: TaskDto, sort: TaskSort): number {
  const { key, dir } = sort;

  const aMissing = isMissing(a, key);
  const bMissing = isMissing(b, key);
  // Deliberately NOT negated for `desc` — missing always sinks.
  if (aMissing !== bMissing) return aMissing ? 1 : -1;

  if (!aMissing) {
    const primary = comparePrimary(a, b, key);
    if (primary !== 0) return dir === 'asc' ? primary : -primary;
  }

  // Tie-breaks always ascend, whatever `dir` is.
  if (key !== 'due') {
    const byDue = compareDueAscNullsLast(a, b);
    if (byDue !== 0) return byDue;
  }
  return compareId(a, b);
}

/**
 * Sorted copy of `rows`. `native` returns the input untouched — "Default"
 * means exactly what the endpoint returned, which is itself stable now that
 * every list query ends on `id`.
 */
export function sortRows(rows: TaskDto[], sort: TaskSort): TaskDto[] {
  if (sort.key === 'native') return rows;
  return [...rows].sort((a, b) => compareTasks(a, b, sort));
}

// --- persistence ---------------------------------------------------------

/**
 * One localStorage key holds every section's choice.
 *
 * v2: keys are SECTION ids (`today.overdue`), not page ids (`today`). The v1
 * blob is abandoned rather than migrated — its keys name a granularity that
 * no longer exists, so every section starts at its default once.
 */
const STORAGE_KEY = 'kos.tasksort.v2';

/**
 * Every sortable list in the Tasks app, as `<screen>.<section>`. The suffix
 * matches the screen's own bucket property name, so an id is traceable to the
 * code that produces its rows.
 */
export const TASK_SECTION_IDS = [
  'today.overdue',
  'today.due',
  'today.followups',
  'today.scheduled',
  'today.closed',
  'upcoming.tomorrow',
  'upcoming.thisWeek',
  'upcoming.nextWeek',
  'upcoming.later',
  'waiting.stale',
  'waiting.dueToday',
  'waiting.upcoming',
  'calendar.created',
  'calendar.due',
  'calendar.closed',
  'calendar.updated',
  'all.tasks',
] as const;

export type TaskSectionId = (typeof TASK_SECTION_IDS)[number];

export function isTaskSectionId(v: unknown): v is TaskSectionId {
  return typeof v === 'string' && (TASK_SECTION_IDS as readonly string[]).includes(v);
}

export type StoredSorts = Partial<Record<TaskSectionId, TaskSort>>;

function isSortDir(v: unknown): v is SortDir {
  return v === 'asc' || v === 'desc';
}

/**
 * Parse the persisted map, dropping anything malformed.
 *
 * Exported (and taking the raw string rather than reading localStorage
 * itself) so it can be asserted without a DOM.
 */
export function readStoredSorts(raw: string | null): StoredSorts {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

  const out: StoredSorts = {};
  for (const [sectionId, value] of Object.entries(parsed as Record<string, unknown>)) {
    // Validate the KEY too — v1 stored page ids, and an id can disappear when
    // a section is renamed. Dropping them here prunes the blob on next write.
    if (!isTaskSectionId(sectionId)) continue;
    if (typeof value !== 'object' || value === null) continue;
    const { key, dir } = value as { key?: unknown; dir?: unknown };
    if (!isTaskSortKey(key) || !isSortDir(dir)) continue;
    out[sectionId] = { key, dir };
  }
  return out;
}

function loadStored(): StoredSorts {
  if (typeof window === 'undefined') return {};
  try {
    return readStoredSorts(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // localStorage unavailable (private mode, quota) — in-memory only.
    return {};
  }
}

function persistSection(sectionId: TaskSectionId, sort: TaskSort): void {
  if (typeof window === 'undefined') return;
  try {
    // Merge, never replace: this screen's state knows nothing about the other
    // screens' sections, and clobbering the blob would reset them.
    const next = { ...loadStored(), [sectionId]: sort };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (private mode, quota) — in-memory only.
  }
}

/** The sections a screen owns, and what each starts on. */
export type SectionDefaults = Partial<Record<TaskSectionId, TaskSortKey>>;

/**
 * Read and write state only. Applying a sort to rows is `TaskSection`'s job —
 * an earlier draft also returned a `sortRowsFor(id, rows)` helper, which left
 * two ways to sort a list and no rule about which to use. That ambiguity is
 * how a `TaskSection` that displayed a sort without applying it got written.
 */
export interface UseSectionSorts {
  sortFor: (sectionId: TaskSectionId) => TaskSort;
  setSortFor: (sectionId: TaskSectionId, next: TaskSort) => void;
}

/**
 * Per-section sort state for one screen, persisted across reloads.
 *
 * Called ONCE per screen with that screen's section defaults, holding a single
 * state object keyed by section id.
 *
 * Deliberately not one hook per section: `useTaskSort` five times in Today
 * would work only while the section list stays a fixed literal. The moment
 * anyone derives it from data or makes one conditional, the hook count changes
 * between renders and React throws. One state object has no such hazard.
 */
export function useSectionSorts(defaults: SectionDefaults): UseSectionSorts {
  // Callers pass a fresh object literal every render. Freeze the first one so
  // seeding and later fallbacks agree, and so the callbacks stay stable.
  const defaultsRef = useRef(defaults);

  const [sorts, setSorts] = useState<StoredSorts>(() => {
    const stored = loadStored();
    const seeded: StoredSorts = {};
    for (const [id, key] of Object.entries(defaultsRef.current) as [
      TaskSectionId,
      TaskSortKey,
    ][]) {
      seeded[id] = stored[id] ?? { key, dir: naturalDir(key) };
    }
    return seeded;
  });

  const sortFor = useCallback(
    (sectionId: TaskSectionId): TaskSort => {
      const current = sorts[sectionId];
      if (current) return current;
      // A section the caller didn't declare: fall back to the server order
      // rather than guessing an ordering for it.
      const key = defaultsRef.current[sectionId] ?? 'native';
      return { key, dir: naturalDir(key) };
    },
    [sorts],
  );

  const setSortFor = useCallback((sectionId: TaskSectionId, next: TaskSort) => {
    setSorts((prev) => ({ ...prev, [sectionId]: next }));
    persistSection(sectionId, next);
  }, []);

  return { sortFor, setSortFor };
}
